import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ICasinoWinningBet, IWinningTicketResponse } from "@finbackoffice/clientbff-client";
import {
    ClientBFFContext,
    formatCasinoProviderName,
    RuntimeConfigContext,
    UserAccountContext,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import classNames from "classnames";
import { TranslationScopes } from "@finbackoffice/enums";
import {
    CasinoGameTypeEnum,
    CasinoWidgetFolderEnum,
    formatAppNameText,
    IWinnersWidgetPropsConfig,
} from "@finbackoffice/fe-core";
import { Settings } from "react-slick";
import Img from "components/base/img/Img";
import Translate from "components/base/translate/Translate";
import { CurrencyFormatter } from "components/base/currency-formater/CurrencyFormater";
import { Svg } from "components/base/svg/Svg";
import CasinoWidgetContainer from "components/pages/casino/base/casino-widget-container/CasinoWidgetContainer";
import SlickSlider from "components/base/slick-slider/SlickSlider";
import { CasinoGameImgFolderType, getCasinoGameImgSrc } from "utils/helpers";
import styles from "./top-winners.module.sass";

type IWinningBetsState = {
    slotBets: ICasinoWinningBet[] | null;
    liveCasinoBets: ICasinoWinningBet[] | null;
    sportBets: IWinningTicketResponse[] | null;
    loading: boolean;
};

enum ViewType {
    Slot = "slot",
    LiveCasino = "livecasino",
    Sport = "sport",
}

type IProps = {
    config: IWinnersWidgetPropsConfig;
};

const TopWinners: FC<IProps> = ({ config }) => {
    const runtimeConfig = useContext(RuntimeConfigContext);
    const ASSETS_URL = useRuntimeConfig("ASSETS_URL");
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const client = useContext(ClientBFFContext);
    const { currentCurrency } = useContext(UserAccountContext);
    const [winningBetsState, setWinningBetsState] = useState<IWinningBetsState>({
        slotBets: [],
        liveCasinoBets: [],
        sportBets: [],
        loading: true,
    });
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slidesOrder, setSlidesOrder] = useState<ViewType[]>([]);
    const imgFolder = COMMON_SITE_CONFIGS.casinoImg;

    const sliderSettings: Settings = useMemo(() => {
        const beforeChangeHandler = (_current: number, next: number) => {
            if (slidesOrder.length > 1) {
                setCurrentSlide(next);
            }
        };
        return {
            dots: false,
            infinite: true,
            autoplay: slidesOrder.length > 1,
            speed: 3000,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            fade: true,
            beforeChange: beforeChangeHandler,
        };
    }, [slidesOrder.length]);

    useEffect(() => {
        const updateSlidesOrder = (newState: IWinningBetsState) => {
            const newSlidesOrder = [];

            if (newState.slotBets) {
                newSlidesOrder.push(ViewType.Slot);
            }
            if (newState.liveCasinoBets) {
                newSlidesOrder.push(ViewType.LiveCasino);
            }
            if (newState.sportBets) {
                newSlidesOrder.push(ViewType.Sport);
            }

            setSlidesOrder(newSlidesOrder);
        };

        const loadData = async () => {
            if (currentCurrency) {
                setWinningBetsState((state) => ({
                    ...state,
                    loading: true,
                }));

                const payload = config.casinoGameId ? { game_id: config.casinoGameId } : undefined;

                try {
                    const [casinoStatsResponse, sportStatsResponse] = await Promise.all([
                        config.slots || config.livecasino || config.casinoGameId
                            ? client.getCasinoWinningBets(payload)
                            : undefined,
                        config.sport ? client.getWinningTickets() : undefined,
                    ]);

                    const updatedState = {
                        slotBets:
                            config.slots && casinoStatsResponse
                                ? casinoStatsResponse.slots.slice(0, 5)
                                : null,
                        liveCasinoBets:
                            config.livecasino && casinoStatsResponse
                                ? casinoStatsResponse.liveCasino.slice(0, 5)
                                : null,
                        sportBets: (config.sport && sportStatsResponse) || null,
                        loading: false,
                    };

                    setWinningBetsState(updatedState);

                    updateSlidesOrder(updatedState);
                } catch (error) {
                    setWinningBetsState((state) => ({
                        ...state,
                        loading: false,
                    }));
                }
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCurrency]);

    const getImgSrcFolder = useCallback(
        (bet: ICasinoWinningBet): CasinoGameImgFolderType | undefined => {
            if (imgFolder && imgFolder === CasinoWidgetFolderEnum.Central) {
                return `${imgFolder}/${formatCasinoProviderName(bet.game_category)}`;
            }

            return undefined;
        },
        [imgFolder],
    );

    const renderCasinoStat = useCallback(
        (bet: ICasinoWinningBet, index: number, viewType: ViewType) => (
            <div key={index} className={styles.topWinner}>
                <Img
                    wrapperClassName={styles.topWinnerImgCasino}
                    source={getCasinoGameImgSrc(
                        runtimeConfig,
                        bet.game_id,
                        viewType === ViewType.Slot
                            ? CasinoGameTypeEnum.VIDEO_SLOTS
                            : CasinoGameTypeEnum.LIVE_CASINO,
                        [],
                        { folder: getImgSrcFolder(bet) },
                    )}
                    alt="winner"
                    width="0"
                    height="0"
                    style={{ width: "100%", height: "auto" }}
                />
                <div>
                    <div>
                        <span>{bet.username}</span>
                        <CurrencyFormatter
                            currency={bet.currency ?? ""}
                            amount={bet.bet_wallet_result_amount}
                            withCode={false}
                            withSymbol
                        />
                    </div>
                    <span>{bet.game_name}</span>
                </div>
            </div>
        ),
        [getImgSrcFolder, runtimeConfig],
    );

    const renderSportStat = useCallback(
        (bet: IWinningTicketResponse, index: number) => {
            return (
                <div key={index} className={styles.topWinner}>
                    <Img
                        wrapperClassName={styles.topWinnerImg}
                        source={`${ASSETS_URL}/${formatAppNameText(
                            COMMON_SITE_CONFIGS.appName,
                        )}/common/base-icons/top-winner-${index + 1}.svg`}
                        alt="winner"
                        width={22}
                        height={26}
                    />
                    <div>
                        <div>
                            <span>{bet.username}</span>
                            <CurrencyFormatter
                                currency={bet.currency ?? ""}
                                amount={bet.amount}
                                withCode={false}
                                withSymbol
                            />
                        </div>
                        <span>{`${bet.eventLocalized?.home_team} vs ${bet.eventLocalized?.away_team}`}</span>
                    </div>
                </div>
            );
        },
        [ASSETS_URL, COMMON_SITE_CONFIGS.appName],
    );

    const renderCasinoContent = useCallback(
        (data: ICasinoWinningBet[], viewType: ViewType) => {
            if (data.length) {
                return (
                    <div className={classNames(styles.topWinnersList)}>
                        {data.map((stat, index) => renderCasinoStat(stat, index, viewType))}
                    </div>
                );
            }
            return (
                <span className="no-game">
                    <Translate tid="no_data" />
                </span>
            );
        },
        [renderCasinoStat],
    );

    const renderSportContent = useCallback(
        (data: IWinningTicketResponse[]) => {
            if (data.length) {
                return (
                    <div className={classNames(styles.topWinnersList)}>
                        {data.map(renderSportStat)}
                    </div>
                );
            }
            return (
                <span className="no-game">
                    <Translate tid="no_data" />
                </span>
            );
        },
        [renderSportStat],
    );

    const renderSlide = useCallback(
        (slide: ViewType, index: number) => {
            switch (slide) {
                case ViewType.Slot:
                    return (
                        !!winningBetsState.slotBets && (
                            <Fragment key={index}>
                                {renderCasinoContent(winningBetsState.slotBets, slide)}
                            </Fragment>
                        )
                    );
                case ViewType.LiveCasino:
                    return (
                        !!winningBetsState.liveCasinoBets && (
                            <Fragment key={index}>
                                {renderCasinoContent(winningBetsState.liveCasinoBets, slide)}
                            </Fragment>
                        )
                    );
                case ViewType.Sport:
                    return (
                        !!winningBetsState.sportBets && (
                            <Fragment key={index}>
                                {renderSportContent(winningBetsState.sportBets)}
                            </Fragment>
                        )
                    );
                default:
                    return null;
            }
        },
        [
            renderCasinoContent,
            renderSportContent,
            winningBetsState.liveCasinoBets,
            winningBetsState.slotBets,
            winningBetsState.sportBets,
        ],
    );

    const renderHeader = useMemo(
        () => (
            <>
                <span className={styles.headerTitle}>
                    {!winningBetsState.loading && (
                        <strong>
                            {config.id ? (
                                <Translate
                                    tid={`casino_${config.id}_title`}
                                    namespace={TranslationScopes.CasinoWidgets}
                                />
                            ) : (
                                !!slidesOrder.length && (
                                    <Translate
                                        tid={`${slidesOrder[currentSlide]}_winners_title`}
                                        namespace={TranslationScopes.Casino}
                                    />
                                )
                            )}
                        </strong>
                    )}
                </span>
                <Svg
                    src={`/${formatAppNameText(
                        COMMON_SITE_CONFIGS.appName,
                    )}/common/base-icons/crown.svg`}
                    wrapper="span"
                    className={styles.crown}
                />
            </>
        ),
        [
            COMMON_SITE_CONFIGS.appName,
            config.id,
            currentSlide,
            slidesOrder,
            winningBetsState.loading,
        ],
    );

    return (
        <CasinoWidgetContainer
            variant="topWinners"
            loading={winningBetsState.loading}
            renderHeader={renderHeader}>
            <div className={styles.sliderContainer}>
                <SlickSlider settings={sliderSettings}>{slidesOrder.map(renderSlide)}</SlickSlider>
            </div>
        </CasinoWidgetContainer>
    );
};

export default TopWinners;
